/**
 * Backtesting page (v1.3.0, Part 6): configure a historical strategy
 * simulation, run it against stored price history, and inspect growth,
 * risk metrics, per-symbol performance, and benchmark comparisons.
 * Every result view carries the "past performance" disclaimer.
 */

import React, { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import { apiClient } from "@/api/client";
import type { BacktestRun, BacktestRunSummary, BacktestStrategyKind } from "@/types";
import LineChart from "@/components/charts/LineChart";
import LoadBenchmarksButton from "@/components/research/LoadBenchmarksButton";

const STRATEGIES: Array<{ value: BacktestStrategyKind; label: string; hint: string }> = [
  { value: "lump_sum", label: "Lump sum", hint: "Invest the initial amount on day one." },
  { value: "dca", label: "Dollar-cost averaging", hint: "Split the initial amount into equal monthly buys." },
  { value: "monthly_contribution", label: "Monthly contributions", hint: "Initial amount, then a fixed amount every month." },
  { value: "quarterly_contribution", label: "Quarterly contributions", hint: "Initial amount, then a fixed amount every quarter." },
  { value: "custom", label: "Custom schedule", hint: "Your own dated contributions, one per line." },
];

function fmtMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  return Number.isFinite(num)
    ? `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : "—";
}

function fmtPct(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  return Number.isFinite(num) ? `${num >= 0 ? "+" : ""}${num.toFixed(2)}%` : "—";
}

function fmtNum(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "—";
}

/** Benchmarks may nest their numbers under `metrics` or carry them flat. */
function benchmarkMetric(entry: Record<string, unknown>, key: string): string | number | null {
  const metrics = entry.metrics as Record<string, unknown> | undefined;
  const value = (metrics?.[key] ?? entry[key]) as string | number | null | undefined;
  return value ?? null;
}

export default function Backtesting(): React.ReactElement {
  const [symbolsText, setSymbolsText] = useState("");
  const [strategy, setStrategy] = useState<BacktestStrategyKind>("lump_sum");
  const [name, setName] = useState("");
  const [initialAmount, setInitialAmount] = useState("10000");
  const [contributionAmount, setContributionAmount] = useState("500");
  const [dcaPeriods, setDcaPeriods] = useState("12");
  const [customScheduleText, setCustomScheduleText] = useState("");
  const [run, setRun] = useState<BacktestRun | null>(null);
  const [history, setHistory] = useState<BacktestRunSummary[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await apiClient.get<BacktestRunSummary[]>("/backtests");
      setHistory(data);
    } catch {
      /* the history list is auxiliary; a failure here shouldn't block the page */
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const handleRun = async (): Promise<void> => {
    setError(null);
    const symbols = symbolsText
      .split(/[\s,]+/)
      .map((symbol) => symbol.trim().toUpperCase())
      .filter(Boolean);
    if (symbols.length === 0) {
      setError("Enter at least one symbol with stored price history.");
      return;
    }

    const customSchedule = customScheduleText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [dateText, amountText] = line.split(/[,\s]+/);
        return { contribution_date: dateText, amount: Number(amountText) };
      });

    setIsRunning(true);
    try {
      const { data } = await apiClient.post<BacktestRun>("/backtests", {
        name: name || null,
        symbols,
        strategy,
        initial_amount: Number(initialAmount) || 0,
        contribution_amount:
          strategy === "monthly_contribution" || strategy === "quarterly_contribution"
            ? Number(contributionAmount) || 0
            : 0,
        dca_periods: Number(dcaPeriods) || 12,
        custom_schedule: strategy === "custom" ? customSchedule : [],
      });
      setRun(data);
      void loadHistory();
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string | Array<{ msg: string }> }>;
      const detail = axiosError.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? detail[0].msg
            : "Unable to run this backtest."
      );
    } finally {
      setIsRunning(false);
    }
  };

  const handleOpenRun = async (runId: string): Promise<void> => {
    try {
      const { data } = await apiClient.get<BacktestRun>(`/backtests/${runId}`);
      setRun(data);
    } catch {
      setError("Unable to load that run.");
    }
  };

  const series = run?.results.series ?? [];
  const benchmarks = run?.results.benchmarks ?? [];
  const symbolPerformance = run?.results.symbol_performance ?? [];
  const showsContribution =
    strategy === "monthly_contribution" || strategy === "quarterly_contribution";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Backtesting</h1>
        <p className="mt-1 text-sm text-mist-400">
          Simulate a strategy over stored price history and compare it against benchmarks.
          Historical performance does not guarantee future results.
        </p>
      </div>

      <LoadBenchmarksButton />

      <div className="panel p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-mist-400">Symbols</span>
            <input
              value={symbolsText}
              onChange={(event) => setSymbolsText(event.target.value)}
              placeholder="AAPL, MSFT"
              className="field-input mt-1 w-full"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-mist-400">Strategy</span>
            <select
              value={strategy}
              onChange={(event) => setStrategy(event.target.value as BacktestStrategyKind)}
              className="field-input mt-1 w-full"
            >
              {STRATEGIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-mist-400">
              {STRATEGIES.find((option) => option.value === strategy)?.hint}
            </span>
          </label>
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-mist-400">Run name (optional)</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Core two-stock DCA"
              className="field-input mt-1 w-full"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-mist-400">Initial amount ($)</span>
            <input
              type="number"
              min="0"
              value={initialAmount}
              onChange={(event) => setInitialAmount(event.target.value)}
              className="field-input mt-1 w-full"
            />
          </label>
          {showsContribution && (
            <label className="block text-sm">
              <span className="text-xs uppercase tracking-wide text-mist-400">
                Contribution per period ($)
              </span>
              <input
                type="number"
                min="0"
                value={contributionAmount}
                onChange={(event) => setContributionAmount(event.target.value)}
                className="field-input mt-1 w-full"
              />
            </label>
          )}
          {strategy === "dca" && (
            <label className="block text-sm">
              <span className="text-xs uppercase tracking-wide text-mist-400">DCA periods (months)</span>
              <input
                type="number"
                min="2"
                max="60"
                value={dcaPeriods}
                onChange={(event) => setDcaPeriods(event.target.value)}
                className="field-input mt-1 w-full"
              />
            </label>
          )}
          {strategy === "custom" && (
            <label className="block text-sm sm:col-span-2">
              <span className="text-xs uppercase tracking-wide text-mist-400">
                Custom schedule — one "YYYY-MM-DD, amount" per line
              </span>
              <textarea
                rows={3}
                value={customScheduleText}
                onChange={(event) => setCustomScheduleText(event.target.value)}
                placeholder={"2024-01-15, 1000\n2024-06-15, 1000"}
                className="field-input mt-1 w-full font-mono"
              />
            </label>
          )}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button onClick={handleRun} disabled={isRunning} className="btn-primary">
            {isRunning ? "Simulating…" : "Run backtest"}
          </button>
          {error && <p className="text-sm text-signal-rose">{error}</p>}
        </div>
      </div>

      {run && (
        <>
          <div className="panel p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-mist-100">
                {run.name ?? "Backtest result"}
              </h2>
              <p className="text-xs text-mist-400">
                {run.start_date} → {run.end_date} · {run.trading_days} trading days ·{" "}
                {run.strategy_kind.replace(/_/g, " ")}
              </p>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Metric label="Contributed" value={fmtMoney(run.total_contributed)} />
              <Metric label="Final value" value={fmtMoney(run.final_value)} />
              <Metric label="Total return" value={fmtPct(run.total_return_pct)} />
              <Metric label="Annualized (IRR)" value={fmtPct(run.annualized_return_pct)} />
              <Metric label="Volatility (ann.)" value={fmtPct(run.volatility_annualized_pct)} />
              <Metric label="Sharpe ratio" value={fmtNum(run.sharpe_ratio)} />
              <Metric label="Max drawdown" value={fmtPct(run.max_drawdown_pct)} />
              <Metric label="Win rate (months)" value={fmtPct(run.win_rate_pct)} />
            </div>

            {series.length > 1 && (
              <div className="mt-6">
                <p className="text-xs uppercase tracking-wide text-mist-400">Portfolio growth</p>
                <div className="mt-2 overflow-x-auto">
                  <LineChart data={series.map((point) => point.value)} width={720} height={160} />
                </div>
              </div>
            )}
          </div>

          {symbolPerformance.length > 0 && (
            <div className="panel overflow-x-auto p-6">
              <h2 className="text-lg font-semibold text-mist-100">Best & worst holdings</h2>
              <table className="mt-4 w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-700 text-xs uppercase tracking-wide text-mist-400">
                    <th className="py-2 pr-4">Symbol</th>
                    <th className="py-2 pr-4 text-right">Contributed</th>
                    <th className="py-2 pr-4 text-right">Final value</th>
                    <th className="py-2 text-right">Return</th>
                  </tr>
                </thead>
                <tbody>
                  {symbolPerformance.map((row) => (
                    <tr key={row.symbol} className="border-b border-ink-800 last:border-0">
                      <td className="py-2 pr-4 font-mono text-signal-teal">{row.symbol}</td>
                      <td className="py-2 pr-4 text-right font-mono text-mist-200">
                        {fmtMoney(row.contributed)}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-mist-200">
                        {fmtMoney(row.final_value)}
                      </td>
                      <td
                        className={`py-2 text-right font-mono ${row.return_pct >= 0 ? "text-signal-teal" : "text-signal-rose"}`}
                      >
                        {fmtPct(row.return_pct)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {benchmarks.length > 0 && (
            <div className="panel overflow-x-auto p-6">
              <h2 className="text-lg font-semibold text-mist-100">Benchmark comparison</h2>
              <table className="mt-4 w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-700 text-xs uppercase tracking-wide text-mist-400">
                    <th className="py-2 pr-4">Benchmark</th>
                    <th className="py-2 pr-4 text-right">Total return</th>
                    <th className="py-2 pr-4 text-right">Annualized</th>
                    <th className="py-2 text-right">Max drawdown</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-ink-800">
                    <td className="py-2 pr-4 text-mist-100">Your strategy</td>
                    <td className="py-2 pr-4 text-right font-mono text-mist-100">
                      {fmtPct(run.total_return_pct)}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono text-mist-100">
                      {fmtPct(run.annualized_return_pct)}
                    </td>
                    <td className="py-2 text-right font-mono text-mist-100">
                      {fmtPct(run.max_drawdown_pct)}
                    </td>
                  </tr>
                  {benchmarks.map((benchmark) => (
                    <tr key={benchmark.key} className="border-b border-ink-800 last:border-0">
                      <td className="py-2 pr-4 text-mist-200">{benchmark.name}</td>
                      {benchmark.available === false ? (
                        <td colSpan={3} className="py-2 text-xs text-mist-400">
                          {benchmark.reason ?? "Not available for this run."}
                        </td>
                      ) : (
                        <>
                          <td className="py-2 pr-4 text-right font-mono text-mist-200">
                            {fmtPct(benchmarkMetric(benchmark, "total_return_pct") as never)}
                          </td>
                          <td className="py-2 pr-4 text-right font-mono text-mist-200">
                            {fmtPct(benchmarkMetric(benchmark, "annualized_return_pct") as never)}
                          </td>
                          <td className="py-2 text-right font-mono text-mist-200">
                            {fmtPct(benchmarkMetric(benchmark, "max_drawdown_pct") as never)}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs leading-relaxed text-mist-400">{run.disclaimer}</p>
        </>
      )}

      <div className="panel p-6">
        <h2 className="text-lg font-semibold text-mist-100">Previous runs</h2>
        {history.length === 0 ? (
          <p className="mt-3 text-sm text-mist-400">
            No runs yet. Ingest price bars for your symbols, then run your first simulation above.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-ink-800">
            {history.map((summary) => (
              <li key={summary.id}>
                <button
                  onClick={() => void handleOpenRun(summary.id)}
                  className="flex w-full items-center justify-between py-2.5 text-left text-sm hover:text-mist-100"
                >
                  <span className="text-mist-200">
                    {summary.name ?? summary.strategy_kind.replace(/_/g, " ")}
                    <span className="ml-2 text-xs text-mist-400">
                      {new Date(summary.created_at).toLocaleDateString()}
                    </span>
                  </span>
                  <span
                    className={`font-mono ${Number(summary.total_return_pct) >= 0 ? "text-signal-teal" : "text-signal-rose"}`}
                  >
                    {fmtPct(summary.total_return_pct)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <p className="text-xs text-mist-400">{label}</p>
      <p className="mt-1 font-mono text-sm text-mist-100">{value}</p>
    </div>
  );
}
